import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Adjustment } from '../../model/project/adjustment';
import { ProjectPlacement } from '../../model/project/projectPlacement';
import Address from '../../model/project/address';
import ProjectHelper from '../../helpers/project-helper';
import { Project } from '../../model/project/project';
import ProductHelper, { TitleOption } from '../../helpers/product-helper';
import { SoldRented } from 'src/app/model/project/sold-rented';
import { Repo } from 'src/app/model/project/repo';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { EX_EDIT_ADJUSTMENT_KEY } from './ex-edit-adjustment-strings/ex-edit-adjustment-strings';
import DateHelper from 'src/app/helpers/date-helper';
import moment from 'moment';
import IPBoard from '../../model/product/ip-board/ip-board';

@Component({
  selector: 'ex-edit-adjustment',
  templateUrl: './ex-edit-adjustment.component.html',
  styleUrls: ['./ex-edit-adjustment.component.scss'],
})
export class ExEditAdjustmentComponent implements OnInit {
  @Input()
  subproject: Adjustment;
  @Input()
  project: Project;

  @Input()
  client = false;

  @Input()
  disabled = false;

  @Input()
  showClientStrings = true;

  ipBoard: IPBoard;

  @Output()
  output = new EventEmitter();

  projectHasIPBoard = false;
  baseProjectPlacement: ProjectPlacement;
  soldRented: SoldRented;
  repo: Repo;
  disableAllInputs = false;
  multipleCompaniesInvolved = false;
  baseProjectTitle: string;
  soldRentedSuggestion: string;

  extraInfoAdjustmentTitle: string;

  showStoreQuestion = false;

  minPlacementDate: Date;
  dateError = false;

  STICKER_OPTIONS = ProductHelper.SOLD_RENTED_OPTIONS;
  OTHER_STICKER_ID = ProductHelper.OTHER_TITLE_ID;
  selectedSticker: TitleOption;
  otherStickerValue: string;

  EX_EDIT_ADJUSTMENT_KEY = EX_EDIT_ADJUSTMENT_KEY;

  constructor() {}

  ngOnInit() {
    this.initializeMinDate();
    if (this.subproject.projectPlacement == null) {
      this.subproject.projectPlacement = new ProjectPlacement();
    }

    if (this.subproject.projectPlacement.address == null) {
      this.subproject.projectPlacement.address = new Address();
    }

    if (this.subproject.type === 'Adjustment') {
      this.extraInfoAdjustmentTitle = 'Omschrijf de aanpassing';
    }
    if (this.subproject.type === 'SoldRented') {
      this.soldRented = <SoldRented>this.subproject;
      this.disableAllInputs = this.client && !this.soldRented.installByInpress;
      this.extraInfoAdjustmentTitle = 'Omschrijf de verkocht/verhuurd sticker';

      //For projects, selection is always saved in otherSticker field
      this.setStickerSelection();

      if (
        this.client &&
        this.project != null &&
        CollectionHelper.isPopulated(this.project.products)
      ) {
        for (const product of this.project.products) {
          if (!StringHelper.isEmpty(product.title)) {
            this.baseProjectTitle = product.title;
            const suggestion = ProjectHelper.getSoldRentedTextSuggestion(
              this.baseProjectTitle
            );
            if (!StringHelper.isEmpty(suggestion)) {
              this.soldRentedSuggestion = `Tekst: ${suggestion}`;
            }
          }
          if (!StringHelper.isEmpty(this.soldRentedSuggestion)) {
            this.subproject.extraInfoAdjustment = StringHelper.isEmpty(
              this.subproject.extraInfoAdjustment
            )
              ? this.soldRentedSuggestion
              : this.subproject.extraInfoAdjustment;
            break;
          }
        }
      }
    }

    if (this.subproject.type === 'Repo') {
      this.repo = <Repo>this.subproject;
      this.disableAllInputs = this.client && !this.repo.repoByInpress;
      this.extraInfoAdjustmentTitle = 'Opmerkingen bij het weghalen';
    }

    if (this.project != null) {
      let ipBoard = this.project.products?.find((p) =>
        ProductHelper.isIPBoard(p)
      );
      if (ipBoard != undefined) {
        this.projectHasIPBoard = true;
        this.ipBoard = ipBoard;
      }

      const baseProject = ProjectHelper.getBaseProject(this.project);
      this.baseProjectPlacement =
        baseProject == null ? null : baseProject.projectPlacement;
    }

    this.multipleCompaniesInvolved = ProjectHelper.multipleInvolvedCompanies(
      this.project
    );
    this.calculateShowStoreQuestion();
  }

  private setStickerSelection() {
    var otherSticker = this.STICKER_OPTIONS.find(
      (value) => value.id === this.soldRented.otherSticker
    );
    this.selectedSticker =
      this.soldRented.signSticker !== null
        ? this.STICKER_OPTIONS.find(
            (value) => value.label === this.soldRented.signSticker.text
          )
        : otherSticker !== undefined
        ? otherSticker
        : this.STICKER_OPTIONS.find(
            (value) => value.id === this.OTHER_STICKER_ID
          );

    if (this.selectedSticker.id === this.OTHER_STICKER_ID) {
      this.otherStickerValue = this.soldRented.otherSticker;
    }
  }

  private initializeMinDate(): void {
    this.minPlacementDate =
      this.client && this.subproject.id == null
        ? DateHelper.getMomentBusinessDayMinProjectPlacement().toDate()
        : null;
  }

  save() {
    this.output.emit(this.subproject);
  }

  setAdjustmentExtraInfo(value) {
    this.subproject.extraInfoAdjustment = value;
    this.save();
  }

  placeASAP(event) {
    this.subproject.projectPlacement.placeASAP = event;
    if (event) {
      delete this.subproject.projectPlacement.placementEarliestDate;
    }
    this.save();
  }

  appointmentChange(value) {
    this.subproject.projectPlacement.appointment = value;
    this.save();
  }

  needAppointmentInfoChange(value) {
    this.subproject.projectPlacement.needAppointmentInfo = value;
    this.save();
  }

  placementEarliestDateChange(event) {
    if (
      this.minPlacementDate != null &&
      this.subproject.projectPlacement.placementEarliestDate <
        this.minPlacementDate
    ) {
      this.dateError = true;
    } else {
      this.dateError = false;
      if (event != null) {
        this.subproject.projectPlacement.placeASAP = false;
      }
      this.save();
    }
  }

  splitCostsChange(value) {
    this.subproject.splitCosts = value;
    this.save();
  }

  installByInpressChange(value) {
    this.soldRented.installByInpress = value;
    this.disableAllInputs = this.client && !value;
    this.save();
  }

  repoByInpressChange(value) {
    this.repo.repoByInpress = value;
    this.disableAllInputs = this.client && !value;
    this.repo.status = value ? 'Nieuw' : 'Gearchiveerd';
    this.save();
  }

  storeChange(value) {
    this.repo.store = value;
    this.save();
  }

  collectKeyChange(value: boolean) {
    this.subproject.projectPlacement.collectKey = value;
    this.save();
  }

  collectKeyExtraInfoChange(value: string) {
    this.subproject.projectPlacement.collectKeyExtraInfo = value;
    this.save();
  }

  getFormDisplayNameLowerCase() {
    return ProjectHelper.getFormDisplayNameLowerCase(this.subproject);
  }

  getFormDisplayArticle() {
    return ProjectHelper.getFormDisplayArticle(this.subproject);
  }

  useStandardLayoutChange(event) {
    this.soldRented.useStandardLayout = event;
    this.save();
  }

  getASAPCheckboxText() {
    return this.subproject.type === 'Repo'
      ? 'Zo snel mogelijk'
      : 'De ' +
          this.getFormDisplayNameLowerCase() +
          ' moet ASAP geplaatst worden';
  }

  private calculateShowStoreQuestion(): void {
    this.showStoreQuestion = ProjectHelper.showStoreQuestion(
      this.subproject,
      this.project
    );
  }

  daysFilter = (d: moment.Moment): boolean => {
    const day = d.isoWeekday();
    return (
      !this.client ||
      (d.isSameOrAfter(this.minPlacementDate) && day !== 6 && day !== 7)
    );
  };

  soldRentedStickerChange(event) {
    this.soldRented.otherSticker = event.value.label;
    this.save();
  }

  setOtherStickerValue() {
    this.soldRented.otherSticker = this.otherStickerValue;
    this.save();
  }

  compareOptions(o1: TitleOption, o2: TitleOption): boolean {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      return o1.id === o2.id;
    } else {
      return false;
    }
  }
}
